import { useState } from 'react';
import CoachingApplicationModal from '../../components/feature/CoachingApplicationModal';

export default function CoachingPage() {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [openFaq, setOpenFaq] = useState<number | null>(null);

  return (
    <div className="min-h-screen" style={{ backgroundColor: '#F7F2EB', fontFamily: "'Inter', sans-serif" }}>

      {/* ── HERO ── */}
      <section className="relative flex items-center overflow-hidden" style={{ minHeight: '85vh' }}>
        <div
          className="absolute inset-0"
          style={{
            backgroundImage: 'url(https://static.readdy.ai/image/6289f550e75cfa8e096b6d5be03ad3c7/1a1a72d896c86ff9945700054ad62caa.png)',
            backgroundSize: 'cover',
            backgroundPosition: 'center center',
            backgroundRepeat: 'no-repeat',
          }}
        />
        <div className="absolute inset-0" style={{ background: 'rgba(8, 15, 40, 0.38)' }} />
        <div className="relative z-10 w-full max-w-4xl mx-auto px-5 md:px-8 py-32 md:py-52 text-center">
          <div
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full mb-8"
            style={{ background: 'rgba(247,242,235,0.12)', backdropFilter: 'blur(8px)', border: '1px solid rgba(212,166,74,0.4)' }}
          >
            <span className="text-xs font-medium tracking-widest uppercase" style={{ color: '#F2C39B' }}>ADHD Coaching · Available Worldwide</span>
          </div>
          <h1
            className="leading-tight mb-6 md:mb-8"
            style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 'clamp(2.2rem, 6vw, 5.5rem)', fontWeight: 700, color: '#F7F2EB' }}
          >
            This is where your<br />
            <em style={{ fontStyle: 'italic', color: '#D4A64A' }}>life actually changes.</em>
          </h1>
          <p className="text-xl md:text-2xl leading-relaxed mb-4 md:mb-6 mx-auto max-w-2xl" style={{ fontFamily: "'Cormorant Garamond', serif", color: 'rgba(247,242,235,0.95)', fontWeight: 500 }}>
            Medication supports your brain. Coaching supports your life.
          </p>
          <p className="text-sm md:text-base leading-relaxed mb-4 mx-auto max-w-xl" style={{ color: 'rgba(247,242,235,0.85)', fontWeight: 400 }}>
            Understanding your ADHD is the first step. But knowing about it and actually living differently — that gap is where most women get stuck.
          </p>
          <p className="text-sm md:text-base leading-relaxed mb-10 md:mb-12 mx-auto max-w-xl" style={{ color: 'rgba(247,242,235,0.75)', fontWeight: 400 }}>
            Coaching is the bridge between knowing and doing. Build real systems, real habits, and a real life that finally fits your brain.
          </p>
          <div className="flex flex-col items-center justify-center gap-3">
            <button
              onClick={() => setIsModalOpen(true)}
              className="px-8 md:px-10 py-4 rounded-full font-medium text-sm md:text-base transition-all duration-300 whitespace-nowrap cursor-pointer shadow-lg"
              style={{ background: 'linear-gradient(135deg, #D4A64A, #C8963E)', color: '#F7F2EB', boxShadow: '0 4px 24px rgba(212,166,74,0.38)' }}
            >
              Apply for Private ADHD Coaching
            </button>
            <p className="text-sm" style={{ color: 'rgba(247,242,235,0.7)' }}>Limited coaching spots available.</p>
            <p className="text-xs mt-1" style={{ color: 'rgba(247,242,235,0.5)', fontStyle: 'italic' }}>Available worldwide — no Oregon residency required.</p>
          </div>
        </div>
      </section>

      {/* ── MEDICATION vs COACHING ── */}
      <section className="py-14 md:py-20" style={{ background: '#F7F2EB', borderBottom: '1px solid rgba(212,166,74,0.18)' }}>
        <div className="max-w-5xl mx-auto px-5 md:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="p-8 rounded-2xl" style={{ background: '#FFFFFF', border: '1px solid rgba(15,31,61,0.1)', boxShadow: '0 2px 20px rgba(15,31,61,0.05)' }}>
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 flex items-center justify-center rounded-full" style={{ background: 'linear-gradient(135deg, #F2C39B, #E7C4A2)' }}>
                  <i className="ri-capsule-line text-lg" style={{ color: '#D4A64A' }} />
                </div>
                <h3 style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: '1.3rem', fontWeight: 700, color: '#0F1F3D' }}>Medication supports your brain</h3>
              </div>
              <p className="text-sm leading-relaxed" style={{ color: '#2E3A4A' }}>
                Medication can improve focus, reduce impulsivity, and quiet some of the mental noise. It is a powerful tool — but it doesn't teach you how to organize your life, build consistent habits, or create systems that actually work for you.
              </p>
            </div>
            <div className="p-8 rounded-2xl" style={{ background: 'linear-gradient(145deg, #0F1F3D, #1F3A6D)', border: '1px solid rgba(212,166,74,0.35)' }}>
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 flex items-center justify-center rounded-full" style={{ background: 'rgba(212,166,74,0.2)' }}>
                  <i className="ri-compass-3-line text-lg" style={{ color: '#D4A64A' }} />
                </div>
                <h3 style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: '1.3rem', fontWeight: 700, color: '#F7F2EB' }}>Coaching supports your life</h3>
              </div>
              <p className="text-sm leading-relaxed" style={{ color: 'rgba(247,242,235,0.82)' }}>
                Coaching is where you build the real-world skills, systems, and structures your ADHD brain needs to thrive. It's practical, action-oriented, and designed specifically for how you think — so your life finally starts working with your brain instead of against it.
              </p>
            </div>
          </div>
          <p className="text-center mt-7 text-sm italic" style={{ color: '#3E5F8E' }}>
            Many women find the most meaningful change happens when medication and coaching work together. Coaching is also available as a standalone for women who are not on medication or who live outside Oregon.
          </p>
        </div>
      </section>

      {/* ── THIS IS FOR YOU IF ── */}
      <section className="py-16 md:py-32" style={{ background: '#F7F2EB' }}>
        <div className="max-w-5xl mx-auto px-5 md:px-8">
          <div className="text-center mb-10 md:mb-16">
            <div className="flex items-center justify-center gap-3 mb-5">
              <div className="h-px w-12" style={{ background: '#D4A64A' }} />
              <span className="text-xs font-medium tracking-widest uppercase" style={{ color: '#D4A64A' }}>Is This For You?</span>
              <div className="h-px w-12" style={{ background: '#D4A64A' }} />
            </div>
            <h2 style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 'clamp(1.9rem, 4vw, 3.5rem)', fontWeight: 700, color: '#0F1F3D', lineHeight: 1.2 }}>
              This Coaching Is For You If...
            </h2>
          </div>

          <div className="max-w-3xl mx-auto">
            <div className="space-y-3 md:space-y-4 mb-8 md:mb-10">
              {[
                'You know what you should be doing, but struggle to consistently follow through.',
                'You feel overwhelmed by responsibilities even though you are capable, intelligent, and trying your best.',
                'You rely on last-minute pressure to get things done.',
                'You feel stuck in cycles of burnout, frustration, and self-criticism.',
                'You want structure, accountability, and clarity that actually works for an ADHD brain.',
                'You want to stop feeling like you are always behind in your own life.',
                "You've tried planners, productivity systems, and self-help advice — but nothing seems to stick.",
              ].map((item, i) => (
                <div key={i} className="flex items-start gap-3 md:gap-4 p-4 md:p-5 rounded-xl transition-all duration-300" style={{ background: '#FFFFFF', border: '1px solid rgba(15,31,61,0.08)', boxShadow: '0 1px 12px rgba(15,31,61,0.04)' }}>
                  <div className="flex-shrink-0 w-6 h-6 flex items-center justify-center rounded-full mt-0.5" style={{ background: 'linear-gradient(135deg, #F2C39B, #E7C4A2)' }}>
                    <i className="ri-check-line text-xs" style={{ color: '#D4A64A' }} />
                  </div>
                  <p className="text-sm md:text-base leading-relaxed" style={{ color: '#2E3A4A' }}>{item}</p>
                </div>
              ))}
            </div>

            <p className="text-center leading-relaxed italic px-4" style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 'clamp(1.1rem, 3vw, 1.35rem)', color: '#3E5F8E', fontWeight: 600 }}>
              "You are not broken. Your brain just needs a different strategy."
            </p>
          </div>
        </div>
      </section>

      {/* ── WHAT WE BUILD TOGETHER ── */}
      <section className="py-16 md:py-32" style={{ background: 'linear-gradient(160deg, #F2C39B22 0%, #F7F2EB 60%)' }}>
        <div className="max-w-6xl mx-auto px-5 md:px-8">
          <div className="text-center mb-12 md:mb-20">
            <div className="flex items-center justify-center gap-3 mb-5">
              <div className="h-px w-12" style={{ background: '#D4A64A' }} />
              <span className="text-xs font-medium tracking-widest uppercase" style={{ color: '#D4A64A' }}>The Coaching Focus</span>
              <div className="h-px w-12" style={{ background: '#D4A64A' }} />
            </div>
            <h2 style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 'clamp(1.9rem, 4vw, 3.5rem)', fontWeight: 700, color: '#0F1F3D', lineHeight: 1.2 }}>
              What We Work On Together
            </h2>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-8">
            {[
              {
                icon: 'ri-settings-3-line',
                title: 'Executive Function Systems',
                body: 'Build personalized planning, organization, and follow-through systems designed specifically for how your ADHD brain works.',
              },
              {
                icon: 'ri-compass-3-line',
                title: 'Clarity and Direction',
                body: 'Identify what actually matters in your life and eliminate the noise that keeps you stuck.',
              },
              {
                icon: 'ri-rocket-line',
                title: 'Accountability and Momentum',
                body: 'Coaching provides external structure so you can consistently move forward.',
              },
              {
                icon: 'ri-heart-pulse-line',
                title: 'Emotional Regulation',
                body: 'Learn tools for managing overwhelm, burnout, rejection sensitivity, and frustration.',
              },
              {
                icon: 'ri-palette-line',
                title: 'Life Design',
                body: 'Create routines and habits that support the life you want instead of constantly reacting to chaos.',
              },
              {
                icon: 'ri-shield-star-line',
                title: 'Confidence and Self-Trust',
                body: 'Learn how to work with your brain instead of constantly fighting it, so you can finally trust yourself to follow through.',
              },
            ].map((item, i) => (
              <div key={i} className="p-6 md:p-8 rounded-2xl" style={{ background: '#FFFFFF', border: '1px solid rgba(15,31,61,0.1)', boxShadow: '0 2px 20px rgba(15,31,61,0.06)' }}>
                <div className="w-12 h-12 md:w-14 md:h-14 flex items-center justify-center rounded-full mb-4 md:mb-6" style={{ background: 'linear-gradient(135deg, #F2C39B, #E7C4A2)' }}>
                  <i className={`${item.icon} text-xl md:text-2xl`} style={{ color: '#D4A64A' }} />
                </div>
                <h3 className="font-semibold mb-2 md:mb-3" style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: '1.15rem', color: '#0F1F3D' }}>{item.title}</h3>
                <p className="text-sm leading-relaxed" style={{ color: '#2E3A4A' }}>{item.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── HOW COACHING WORKS ── */}
      <section className="py-16 md:py-32" style={{ background: '#F7F2EB' }}>
        <div className="max-w-5xl mx-auto px-5 md:px-8">
          <div className="text-center mb-10 md:mb-16">
            <div className="flex items-center justify-center gap-3 mb-5">
              <div className="h-px w-12" style={{ background: '#D4A64A' }} />
              <span className="text-xs font-medium tracking-widest uppercase" style={{ color: '#D4A64A' }}>The Experience</span>
              <div className="h-px w-12" style={{ background: '#D4A64A' }} />
            </div>
            <h2 style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 'clamp(1.9rem, 4vw, 3.5rem)', fontWeight: 700, color: '#0F1F3D', lineHeight: 1.2 }}>
              The Coaching Experience
            </h2>
            <p className="mt-4 md:mt-6 text-lg md:text-xl leading-relaxed max-w-2xl mx-auto" style={{ fontFamily: "'Cormorant Garamond', serif", color: '#3E5F8E', fontWeight: 500 }}>
              A structured 3-month experience designed to create real, lasting change.
            </p>
          </div>

          <div className="p-6 md:p-12 rounded-3xl mb-8 md:mb-10" style={{ background: '#FFFFFF', border: '1px solid rgba(15,31,61,0.1)', boxShadow: '0 4px 30px rgba(15,31,61,0.08)' }}>
            <div className="space-y-5 md:space-y-6">
              {[
                { icon: 'ri-user-heart-line', label: '12 private 1:1 coaching sessions with Lauren' },
                { icon: 'ri-lightbulb-line', label: 'A personalized ADHD strategy and life systems designed specifically for your brain' },
                { icon: 'ri-tools-line', label: 'Support implementing practical tools and routines' },
                { icon: 'ri-calendar-check-line', label: "Accountability and guidance between sessions so momentum doesn't disappear after the call" },
                { icon: 'ri-trophy-line', label: 'A collaborative process focused on practical, real-life results' },
              ].map((item, i) => (
                <div key={i} className="flex items-start gap-4 md:gap-5">
                  <div className="flex-shrink-0 w-11 h-11 md:w-12 md:h-12 flex items-center justify-center rounded-full" style={{ background: 'linear-gradient(135deg, #F2C39B, #E7C4A2)' }}>
                    <i className={`${item.icon} text-lg md:text-xl`} style={{ color: '#D4A64A' }} />
                  </div>
                  <p className="text-sm md:text-base leading-relaxed pt-2.5" style={{ color: '#2E3A4A' }}>{item.label}</p>
                </div>
              ))}
            </div>
          </div>

          <p className="text-center text-base md:text-lg leading-relaxed italic px-4" style={{ fontFamily: "'Cormorant Garamond', serif", color: '#3E5F8E', fontWeight: 500 }}>
            This work is about building a life that finally feels sustainable — not exhausting.
          </p>
        </div>
      </section>

      {/* ── ABOUT LAUREN ── */}
      <section className="py-16 md:py-32" style={{ background: 'linear-gradient(135deg, #0F1F3D 0%, #1F3A6D 50%, #0F1F3D 100%)' }}>
        <div className="max-w-6xl mx-auto px-5 md:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-10 md:gap-20 items-center">
            {/* Image */}
            <div className="relative mx-auto w-full max-w-sm md:max-w-none">
              <div className="absolute -inset-4 rounded-3xl" style={{ background: 'linear-gradient(135deg, rgba(212,166,74,0.3), rgba(242,195,155,0.2))', filter: 'blur(20px)' }} />
              <img
                src="https://static.readdy.ai/image/6289f550e75cfa8e096b6d5be03ad3c7/db68a039b248cf74ecfb314b9464ae05.jpeg"
                alt="Lauren — Psychiatric Nurse Practitioner"
                className="relative w-full rounded-3xl object-cover object-top"
                style={{ height: 420, border: '2px solid rgba(212,166,74,0.4)' }}
              />
              <div className="absolute -bottom-4 -right-2 md:-bottom-5 md:-right-5 px-4 md:px-6 py-3 md:py-4 rounded-2xl" style={{ background: 'linear-gradient(135deg, #D4A64A, #C8963E)', boxShadow: '0 8px 30px rgba(212,166,74,0.4)' }}>
                <p className="text-xs font-medium" style={{ color: 'rgba(247,242,235,0.8)' }}>Board Certified</p>
                <p className="text-sm font-bold" style={{ color: '#F7F2EB' }}>PMHNP-BC</p>
              </div>
            </div>

            {/* Text */}
            <div className="mt-6 md:mt-0">
              <div className="flex items-center gap-3 mb-5 md:mb-6">
                <div className="h-px w-12" style={{ background: '#D4A64A' }} />
                <span className="text-xs font-medium tracking-widest uppercase" style={{ color: '#D4A64A' }}>Meet Your Coach</span>
              </div>
              <h2 style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 'clamp(1.8rem, 3.5vw, 3rem)', fontWeight: 700, color: '#F7F2EB', lineHeight: 1.2 }}>
                Meet Your Coach
              </h2>

              <div className="mt-6 md:mt-8 space-y-4 md:space-y-5 text-sm md:text-base leading-relaxed" style={{ color: 'rgba(247,242,235,0.82)' }}>
                <p>
                  Lauren is a psychiatric nurse practitioner who specializes in ADHD in women. After working with countless patients who felt misunderstood, overwhelmed, or dismissed, she saw a clear pattern: many women needed more than medication or therapy alone.
                </p>
                <p>
                  They needed strategy, structure, and support designed specifically for how their brains work.
                </p>
                <p>
                  Coaching bridges the gap between understanding ADHD and actually learning how to live well with it.
                </p>
              </div>

              <button
                onClick={() => window.REACT_APP_NAVIGATE('/meet-lauren')}
                className="inline-flex items-center gap-2 mt-8 md:mt-10 px-7 md:px-8 py-3 md:py-4 rounded-full font-medium text-sm transition-all duration-300 whitespace-nowrap cursor-pointer"
                style={{ border: '1.5px solid rgba(212,166,74,0.6)', color: '#D4A64A' }}
              >
                Read Full Bio <i className="ri-arrow-right-line" />
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* ── TESTIMONIALS ── */}
      <section className="py-16 md:py-32" style={{ background: 'linear-gradient(160deg, #F2C39B22 0%, #F7F2EB 60%)' }}>
        <div className="max-w-6xl mx-auto px-5 md:px-8">
          <div className="text-center mb-12 md:mb-20">
            <div className="flex items-center justify-center gap-3 mb-5">
              <div className="h-px w-12" style={{ background: '#D4A64A' }} />
              <span className="text-xs font-medium tracking-widest uppercase" style={{ color: '#D4A64A' }}>Client Stories</span>
              <div className="h-px w-12" style={{ background: '#D4A64A' }} />
            </div>
            <h2 style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 'clamp(1.9rem, 4vw, 3.5rem)', fontWeight: 700, color: '#0F1F3D', lineHeight: 1.2 }}>
              What Women Say After Coaching
            </h2>
            <p className="mt-4 text-sm md:text-base leading-relaxed max-w-xl mx-auto" style={{ color: '#2E3A4A' }}>
              Real experiences from women who have worked with Lauren.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-5 md:gap-8">
            {[
              {
                headline: '"I finally feel in control of my life."',
                body: 'Before working with Lauren, I constantly felt overwhelmed and behind no matter how hard I tried. She helped me create systems that actually work with my ADHD brain. For the first time in years, I feel calm and capable.',
                name: 'Sarah',
                role: 'Marketing Manager',
              },
              {
                headline: '"I stopped blaming myself."',
                body: 'I always thought I was just bad at life. Lauren helped me understand how my ADHD actually works and showed me tools that make everyday things manageable. It completely changed how I see myself.',
                name: 'Megan',
                role: 'Small Business Owner',
              },
              {
                headline: '"My brain finally makes sense."',
                body: "The biggest shift for me was learning strategies that actually fit the way my brain works. I'm getting more done, feeling less overwhelmed, and my confidence has improved dramatically.",
                name: 'Rachel',
                role: 'Working Mom',
              },
            ].map((card, i) => (
              <div
                key={i}
                className="p-7 md:p-10 rounded-2xl flex flex-col gap-5 md:gap-6"
                style={{ background: '#FFFFFF', border: '1px solid rgba(15,31,61,0.1)', boxShadow: '0 2px 20px rgba(15,31,61,0.06)' }}
              >
                <div className="w-10 h-10 flex items-center justify-center rounded-full flex-shrink-0" style={{ background: 'linear-gradient(135deg, #F2C39B, #E7C4A2)' }}>
                  <i className="ri-double-quotes-l text-lg" style={{ color: '#D4A64A' }} />
                </div>
                <h3 style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: '1.2rem', fontWeight: 700, color: '#0F1F3D', lineHeight: 1.3 }}>
                  {card.headline}
                </h3>
                <p className="text-sm leading-relaxed flex-1" style={{ color: '#2E3A4A' }}>{card.body}</p>
                <div className="pt-4" style={{ borderTop: '1px solid rgba(212,166,74,0.2)' }}>
                  <p className="text-sm font-semibold" style={{ color: '#0F1F3D' }}>{card.name}</p>
                  <p className="text-xs mt-0.5" style={{ color: '#3E5F8E' }}>{card.role}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── WHAT CHANGES AFTER COACHING ── */}
      <section className="py-16 md:py-32" style={{ background: '#F7F2EB' }}>
        <div className="max-w-6xl mx-auto px-5 md:px-8">
          <div className="text-center mb-10 md:mb-16">
            <div className="flex items-center justify-center gap-3 mb-5">
              <div className="h-px w-12" style={{ background: '#D4A64A' }} />
              <span className="text-xs font-medium tracking-widest uppercase" style={{ color: '#D4A64A' }}>Real Transformations</span>
              <div className="h-px w-12" style={{ background: '#D4A64A' }} />
            </div>
            <h2 style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 'clamp(1.9rem, 4vw, 3.5rem)', fontWeight: 700, color: '#0F1F3D', lineHeight: 1.2 }}>
              What Changes for Women After Coaching
            </h2>
            <p className="mt-4 md:mt-5 text-base md:text-lg leading-relaxed max-w-xl mx-auto" style={{ fontFamily: "'Cormorant Garamond', serif", color: '#3E5F8E', fontWeight: 500 }}>
              Small shifts in strategy can create big changes in daily life.
            </p>
            <p className="mt-3 text-sm" style={{ color: '#2E3A4A', opacity: 0.7 }}>
              Reflections shared by women who have completed ADHD coaching.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-5 md:gap-8">
            {[
              {
                icon: 'ri-checkbox-circle-line',
                headline: '"For the first time, I actually follow through."',
                body: "Before coaching I constantly felt behind and overwhelmed. Lauren helped me build simple systems that actually work with my brain. I'm no longer living in last-minute panic mode.",
              },
              {
                icon: 'ri-brain-line',
                headline: '"I finally understand my brain."',
                body: 'Instead of constantly blaming myself, I finally understand how my ADHD brain works. Now I have tools that make everyday life manageable.',
              },
              {
                icon: 'ri-leaf-line',
                headline: '"My life finally feels calmer."',
                body: 'The constant mental chaos and burnout are finally improving. I feel more organized, more confident, and far less overwhelmed.',
              },
              {
                icon: 'ri-sun-line',
                headline: '"I stopped feeling like I was failing at life."',
                body: 'Working with someone who truly understands ADHD in women changed everything. I finally feel hopeful about my future again.',
              },
            ].map((card, i) => (
              <div
                key={i}
                className="p-6 md:p-10 rounded-2xl flex flex-col gap-4 md:gap-5"
                style={{ background: '#FFFFFF', border: '1px solid rgba(15,31,61,0.1)', boxShadow: '0 2px 20px rgba(15,31,61,0.06)' }}
              >
                <div className="flex items-start gap-4">
                  <div className="flex-shrink-0 w-11 h-11 md:w-12 md:h-12 flex items-center justify-center rounded-full" style={{ background: 'linear-gradient(135deg, #F2C39B, #E7C4A2)' }}>
                    <i className={`${card.icon} text-lg md:text-xl`} style={{ color: '#D4A64A' }} />
                  </div>
                  <h3 className="pt-1.5" style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 'clamp(1.05rem, 2.5vw, 1.25rem)', fontWeight: 700, color: '#0F1F3D', lineHeight: 1.3 }}>
                    {card.headline}
                  </h3>
                </div>
                <p className="text-sm leading-relaxed" style={{ color: '#2E3A4A' }}>{card.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── INVESTMENT ── */}
      <section className="py-16 md:py-32" style={{ background: '#F7F2EB' }}>
        <div className="max-w-4xl mx-auto px-5 md:px-8">
          <div className="text-center mb-10 md:mb-16">
            <div className="flex items-center justify-center gap-3 mb-5">
              <div className="h-px w-12" style={{ background: '#D4A64A' }} />
              <span className="text-xs font-medium tracking-widest uppercase" style={{ color: '#D4A64A' }}>Investment</span>
              <div className="h-px w-12" style={{ background: '#D4A64A' }} />
            </div>
            <h2 style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 'clamp(1.9rem, 4vw, 3.5rem)', fontWeight: 700, color: '#0F1F3D', lineHeight: 1.2 }}>
              Investment
            </h2>
            <p className="mt-4 text-base md:text-lg leading-relaxed max-w-xl mx-auto" style={{ fontFamily: "'Cormorant Garamond', serif", color: '#3E5F8E', fontWeight: 500 }}>
              A structured coaching experience designed to create lasting change.
            </p>
          </div>

          <div className="p-6 md:p-12 rounded-3xl" style={{ background: '#FFFFFF', border: '1px solid rgba(15,31,61,0.1)', boxShadow: '0 4px 30px rgba(15,31,61,0.08)' }}>
            <h3 className="text-xl md:text-2xl font-semibold mb-6 md:mb-8 text-center" style={{ fontFamily: "'Cormorant Garamond', serif", color: '#0F1F3D' }}>
              The ADHD Momentum Coaching Experience
            </h3>

            <div className="space-y-3 md:space-y-4 mb-8 md:mb-10">
              {[
                '12 private coaching sessions to build systems that actually stick',
                'Personalized ADHD strategies designed specifically for your brain',
                'Support implementing practical tools and routines',
                'Accountability and guidance between sessions so progress continues between meetings',
              ].map((item, i) => (
                <div key={i} className="flex items-start gap-3 md:gap-4">
                  <div className="flex-shrink-0 w-6 h-6 flex items-center justify-center rounded-full mt-0.5" style={{ background: 'linear-gradient(135deg, #F2C39B, #E7C4A2)' }}>
                    <i className="ri-check-line text-xs" style={{ color: '#D4A64A' }} />
                  </div>
                  <p className="text-sm md:text-base" style={{ color: '#2E3A4A' }}>{item}</p>
                </div>
              ))}
            </div>

            <div className="text-center py-6 md:py-8 mb-5 md:mb-6" style={{ borderTop: '1px solid rgba(15,31,61,0.1)', borderBottom: '1px solid rgba(15,31,61,0.1)' }}>
              <p className="text-sm font-medium mb-2" style={{ color: '#3E5F8E' }}>Investment</p>
              <p className="font-bold mb-3" style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 'clamp(2rem, 6vw, 2.5rem)', color: '#D4A64A' }}>$3,000 total</p>
              <p className="text-sm md:text-base" style={{ color: '#2E3A4A' }}>or $1,100 per month for three months</p>
            </div>

            <p className="text-center text-sm italic mb-6 md:mb-8 px-2" style={{ color: '#3E5F8E' }}>
              I only work with a limited number of coaching clients at a time to ensure focused attention and meaningful progress.
            </p>

            <div className="flex justify-center">
              <button
                onClick={() => setIsModalOpen(true)}
                className="px-8 md:px-10 py-4 rounded-full font-medium text-sm md:text-base transition-all duration-300 whitespace-nowrap cursor-pointer shadow-lg"
                style={{ background: 'linear-gradient(135deg, #D4A64A, #C8963E)', color: '#F7F2EB', boxShadow: '0 4px 24px rgba(212,166,74,0.38)' }}
              >
                Apply for Coaching
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* ── RISK REVERSAL ── */}
      <section className="py-16 md:py-32" style={{ background: 'linear-gradient(160deg, #F2C39B22 0%, #F7F2EB 60%)' }}>
        <div className="max-w-4xl mx-auto px-5 md:px-8">
          <div className="text-center mb-10 md:mb-12">
            <div className="flex items-center justify-center gap-3 mb-5">
              <div className="h-px w-12" style={{ background: '#D4A64A' }} />
              <span className="text-xs font-medium tracking-widest uppercase" style={{ color: '#D4A64A' }}>Your Protection</span>
              <div className="h-px w-12" style={{ background: '#D4A64A' }} />
            </div>
            <h2 style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 'clamp(1.8rem, 3.5vw, 3rem)', fontWeight: 700, color: '#0F1F3D', lineHeight: 1.2 }}>
              Commit With Confidence
            </h2>
          </div>

          <div className="p-6 md:p-12 rounded-3xl" style={{ background: '#FFFFFF', border: '1px solid rgba(15,31,61,0.1)', boxShadow: '0 4px 30px rgba(15,31,61,0.08)' }}>
            <div className="flex items-start gap-5 md:gap-6 mb-6 md:mb-8">
              <div className="flex-shrink-0 w-12 h-12 md:w-14 md:h-14 flex items-center justify-center rounded-full" style={{ background: 'linear-gradient(135deg, #F2C39B, #E7C4A2)' }}>
                <i className="ri-shield-check-line text-xl md:text-2xl" style={{ color: '#D4A64A' }} />
              </div>
              <div>
                <p className="text-sm md:text-base leading-relaxed mb-4" style={{ color: '#2E3A4A' }}>
                  If after your first two coaching sessions you feel you are not gaining meaningful clarity, momentum, or practical systems for your ADHD, simply let me know and I will refund the remaining portion of your investment.
                </p>
                <p className="text-sm md:text-base leading-relaxed" style={{ color: '#2E3A4A' }}>
                  I only want to work with clients who feel supported, empowered, and genuinely helped by this process.
                </p>
              </div>
            </div>
            <p className="text-sm italic text-center" style={{ color: '#3E5F8E', borderTop: '1px solid rgba(212,166,74,0.2)', paddingTop: '1.5rem' }}>
              Coaching works best when there is real alignment, and I want you to feel confident in your decision.
            </p>
          </div>
        </div>
      </section>

      {/* ── COACHING FAQ ── */}
      <section className="py-16 md:py-28" style={{ background: '#F7F2EB' }}>
        <div className="max-w-4xl mx-auto px-5 md:px-8">
          <div className="text-center mb-12">
            <div className="flex items-center justify-center gap-3 mb-5">
              <div className="h-px w-12" style={{ background: '#D4A64A' }} />
              <span className="text-xs font-medium tracking-widest uppercase" style={{ color: '#D4A64A' }}>FAQ</span>
              <div className="h-px w-12" style={{ background: '#D4A64A' }} />
            </div>
            <h2 style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 'clamp(1.8rem, 4vw, 3.5rem)', fontWeight: 700, color: '#0F1F3D', lineHeight: 1.2 }}>
              Questions about coaching
            </h2>
          </div>
          <div className="space-y-3">
            {[
              {
                q: 'Is coaching available outside of Oregon?',
                a: 'Yes — ADHD coaching is available to women everywhere. There is no Oregon residency requirement for coaching. It is offered via secure video sessions regardless of where you live.',
              },
              {
                q: 'Do I need a formal ADHD diagnosis to start coaching?',
                a: "No diagnosis is required to begin coaching. If you are in Oregon and want a formal evaluation first, Lauren can help with that too. But if you're outside Oregon or simply ready to start building better systems, coaching is open to you now.",
              },
              {
                q: 'How is ADHD coaching different from therapy?',
                a: 'Coaching is action-oriented and focused on practical strategy — building systems, habits, and structures that help you function better day to day. Therapy typically focuses on emotional processing and mental health treatment. Coaching is not therapy and does not replace it, but for many women it fills an important gap that therapy alone does not address.',
              },
              {
                q: "I'm already on medication. Do I still need coaching?",
                a: "Medication can quiet the noise — but it doesn't teach you how to organize your life, build consistent habits, or create structures that stick. Many women find that coaching is the missing piece even after medication starts working. That's exactly what coaching is designed for.",
              },
              {
                q: 'What if coaching isn\'t the right fit?',
                a: "If after your first two sessions you don't feel you're gaining meaningful clarity and momentum, simply let Lauren know and you'll receive a refund for the remaining portion of your investment. The goal is your real progress — not a sales commitment.",
              },
              {
                q: 'How do I get started?',
                a: 'Click "Apply for Coaching" to fill out the intake form. Lauren reviews all applications personally and will be in touch to discuss whether coaching is the right fit for you right now.',
              },
            ].map((item, i) => (
              <div
                key={i}
                className="rounded-2xl overflow-hidden cursor-pointer"
                style={{ background: '#FFFFFF', border: '1px solid rgba(15,31,61,0.1)' }}
                onClick={() => setOpenFaq(openFaq === i ? null : i)}
              >
                <div className="flex items-center justify-between p-5 md:p-6 gap-4">
                  <h3 className="font-semibold" style={{ fontFamily: "'Cormorant Garamond', serif", color: '#0F1F3D', fontSize: '1.05rem' }}>{item.q}</h3>
                  <div
                    className="flex-shrink-0 w-7 h-7 flex items-center justify-center rounded-full transition-all duration-200"
                    style={{ background: openFaq === i ? 'linear-gradient(135deg, #D4A64A, #C8963E)' : 'rgba(212,166,74,0.1)' }}
                  >
                    <i className={`${openFaq === i ? 'ri-subtract-line' : 'ri-add-line'} text-sm`} style={{ color: openFaq === i ? '#F7F2EB' : '#D4A64A' }} />
                  </div>
                </div>
                {openFaq === i && (
                  <div className="px-5 md:px-6 pb-5 md:pb-6">
                    <p className="text-sm leading-relaxed" style={{ color: '#2E3A4A' }}>{item.a}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── FINAL CTA ── */}
      <section className="relative py-24 md:py-40 overflow-hidden">
        <div
          className="absolute inset-0"
          style={{ background: 'linear-gradient(160deg, #0F1F3D 0%, #1F3A6D 25%, #3E5F8E 50%, #F2C39B 80%, #E7C4A2 100%)' }}
        />
        <div className="absolute inset-0" style={{ background: 'rgba(8,10,30,0.22)' }} />

        {[
          { top: '15%', left: '10%' }, { top: '25%', left: '80%' }, { top: '60%', left: '5%' },
          { top: '70%', left: '90%' }, { top: '40%', left: '95%' }, { top: '80%', left: '20%' },
          { top: '10%', left: '55%' }, { top: '50%', left: '50%' },
        ].map((s, i) => (
          <div key={i} className="absolute w-1 h-1 rounded-full bg-white" style={{ top: s.top, left: s.left, opacity: 0.6 }} />
        ))}

        <div className="relative z-10 max-w-4xl mx-auto px-5 md:px-8 text-center">
          <h2 style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 'clamp(2rem, 5.5vw, 5rem)', fontWeight: 700, color: '#F7F2EB', lineHeight: 1.15, textShadow: '0 2px 24px rgba(8,10,30,0.5)' }}>
            Ready to stop surviving<br />and start actually living?
          </h2>

          <p className="mt-5 md:mt-6 text-base md:text-lg leading-relaxed max-w-2xl mx-auto" style={{ color: 'rgba(247,242,235,0.88)' }}>
            Coaching is designed for women who are ready to stop surviving and start creating a life that actually fits who they are.
          </p>

          <div className="mt-10 md:mt-12 flex flex-col items-center gap-4">
            <button
              onClick={() => setIsModalOpen(true)}
              className="inline-flex items-center gap-2 px-8 md:px-10 py-4 rounded-full font-medium text-sm md:text-base transition-all duration-300 whitespace-nowrap cursor-pointer shadow-lg"
              style={{ background: 'linear-gradient(135deg, #D4A64A, #C8963E)', color: '#F7F2EB', boxShadow: '0 4px 24px rgba(212,166,74,0.45)' }}
            >
              Take Back Control
            </button>
            <p className="text-xs" style={{ color: 'rgba(247,242,235,0.5)', fontStyle: 'italic' }}>Available worldwide — not limited to Oregon</p>
          </div>
        </div>
      </section>

      {/* ── FOOTER ── */}
      <footer style={{ background: '#F2C39B22', borderTop: '1px solid rgba(212,166,74,0.25)' }}>
        <div className="max-w-6xl mx-auto px-5 md:px-8 py-12 md:py-20">
          <div className="grid grid-cols-1 md:grid-cols-10 gap-8 md:gap-12 mb-10 md:mb-14">
            <div className="md:col-span-4">
              <img
                src="https://static.readdy.ai/image/6289f550e75cfa8e096b6d5be03ad3c7/ad8438fb476efc73746cd771a67dd6a9.png"
                alt="North Star ADHD"
                className="h-14 md:h-16 w-auto mb-5 md:mb-6"
              />
              <p className="text-sm leading-relaxed mb-6 md:mb-8" style={{ color: '#2E3A4A' }}>
                Specialized psychiatric care helping high-achieving women understand their ADHD, reduce shame, and build lives that work with their brilliant brains.
              </p>
              <div className="flex items-center gap-3">
                {['ri-instagram-line', 'ri-facebook-fill', 'ri-linkedin-fill'].map((icon, i) => (
                  <a key={i} href="#" className="w-9 h-9 flex items-center justify-center rounded-full transition-all duration-300 cursor-pointer" style={{ border: '1.5px solid rgba(212,166,74,0.4)', color: '#D4A64A' }}>
                    <i className={icon} />
                  </a>
                ))}
              </div>
            </div>

            <div className="md:col-span-3">
              <h4 className="text-xs font-medium tracking-widest uppercase mb-4 md:mb-6" style={{ color: '#D4A64A' }}>Contact</h4>
              <div className="space-y-3 text-sm" style={{ color: '#2E3A4A' }}>
                <p>Serving Oregon via telepsychiatry</p>
                <p>(971) 260-2121</p>
                <p>Fax: (971) 260-2122</p>
                <p>hello@northstaradhd.com</p>
              </div>
            </div>

            <div className="md:col-span-3">
              <h4 className="text-xs font-medium tracking-widest uppercase mb-4 md:mb-6" style={{ color: '#D4A64A' }}>Quick Links</h4>
              <div className="space-y-3">
                {[
                  { label: 'ADHD Evaluations', to: '/adhd-evaluations' },
                  { label: 'Membership', to: '/membership' },
                  { label: 'ADHD Coaching', to: '/coaching' },
                  { label: 'Meet Lauren', to: '/meet-lauren' },
                  { label: 'Book Appointment', to: '/book-appointment' },
                ].map((l) => (
                  <button
                    key={l.to}
                    onClick={() => window.REACT_APP_NAVIGATE(l.to)}
                    className="block text-sm transition-colors duration-300 cursor-pointer text-left"
                    style={{ color: '#2E3A4A' }}
                  >
                    {l.label}
                  </button>
                ))}
              </div>
            </div>
          </div>

          <div className="pt-6 md:pt-8 flex flex-col md:flex-row items-center justify-between gap-4" style={{ borderTop: '1px solid rgba(212,166,74,0.2)' }}>
            <p className="text-xs text-center md:text-left" style={{ color: '#3E5F8E' }}>© 2025 North Star ADHD Psychiatry for Women. All rights reserved.</p>
            <div className="flex items-center gap-5 md:gap-6">
              {['Terms', 'Privacy', 'Accessibility'].map((t) => (
                <a key={t} href="#" className="text-xs transition-colors duration-300 cursor-pointer" style={{ color: '#3E5F8E' }}>{t}</a>
              ))}
            </div>
          </div>
        </div>
      </footer>

      {/* Coaching Application Modal */}
      <CoachingApplicationModal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} />
    </div>
  );
}
